import React from "react";
import { useDrop } from "react-dnd";
import styled, { css } from "styled-components";
import Styles from "@themes/Styles";
import { ItemTypes } from "../../drag-and-drop/DragAndDropConstants";
import { TaskDragItem, TaskDropEventData } from "../../drag-and-drop/DragAndDropTypes";
import { draggedTaskEvent$ } from "../TaskManagementGlobals";
import { IdentifiableTask } from "../TaskManagementTypes";

export const DROPPABLE_TASK_SPOT_MIN_HEIGHT = 10;

const Container = styled.div`
    display: flex;
    align-items: stretch;
    min-height: ${DROPPABLE_TASK_SPOT_MIN_HEIGHT}px;
    padding: 2px 20px;
`;
const Highlight = styled.div<{
    /**
     * If the drop target should be visible. Should only happen while dragging.
     */
    isVisible: boolean;
    /**
     * If this is the target that is being hovered over with the intention to drop.
     */
    isTargetedDropTarget: boolean;
    addBuffer: boolean;
}>`
    // Necessary to set the z-index so that the drop target is above the TaskItem.
    // This is due to the fact that since the TaskItems and DroppableTaskSpots are
    // rendered in a list, the TaskItems will be rendered above the DroppableTaskSpots.
    // Without this, the TaskItem will be above the drop target and the drop target will
    // not detect when the cursor enters it.
    z-index: 10000;
    flex: 1;
    opacity: 40%;
    background-color: ${({ isTargetedDropTarget, theme }) =>
        isTargetedDropTarget ? theme.taskDropTargetColor : "transparent"};
    border-radius: ${Styles.borderRadiusSharp};
    position: relative;
    ${({ addBuffer: addBottomBuffer, isVisible }) =>
        addBottomBuffer &&
        css`
            // Note we need :before and :after so that the drop target detection handles
            // when dragging upward from below the TaskItem and when dragging downward from
            // above the TaskItem.
            &:after {
                content: "";
                position: absolute;
                // Only extend 10px above if the drop target is visible so that the
                // area where a user can grab the TaskItem isn't impacted by the drop target.
                top: ${isVisible ? "-30px" : "0"};
                bottom: ${isVisible ? "-30px" : "0"};
                left: 0;
                right: 0;
            }
        `}
`;

interface DroppableTaskSpotProps {
    /**
     * Handles the drop event for a task dropped on this spot.
     */
    readonly onDrop: (data: TaskDropEventData) => void;
    /**
     * The task that is above this droppable spot.
     * Will be undefined if the spot is at the top of the list.
     */
    readonly upperBoundTask?: IdentifiableTask;
    /**
     * The task that is below this droppable spot.
     * Will be undefined if the spot is at the bottom of the list.
     */
    readonly lowerBoundTask?: IdentifiableTask;
    readonly className?: string;
    readonly addBuffer?: boolean;
}
function DroppableTaskSpot(props: DroppableTaskSpotProps) {
    const [{ isOver, canDrop }, drop] = useDrop({
        accept: ItemTypes.TASK,
        canDrop: () => true,
        drop: (dragItem: TaskDragItem) => {
            if (isOver && canDrop) {
                props.onDrop({
                    dragItem: dragItem,
                    upperBoundTask: props.upperBoundTask,
                    lowerBoundTask: props.lowerBoundTask,
                });
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });

    const shouldHighlightForDrop =
        draggedTaskEvent$.value != null &&
        draggedTaskEvent$.value.type === "singular" &&
        (!props.upperBoundTask ||
            (props.upperBoundTask && props.upperBoundTask.id != draggedTaskEvent$.value.taskId)) &&
        (!props.lowerBoundTask || (props.lowerBoundTask && props.lowerBoundTask.id != draggedTaskEvent$.value.taskId));

    return (
        <Container className={props.className}>
            <Highlight
                ref={drop}
                isVisible={shouldHighlightForDrop}
                isTargetedDropTarget={shouldHighlightForDrop ? isOver : false}
                addBuffer={props.addBuffer ?? true}
            ></Highlight>
        </Container>
    );
}

export default DroppableTaskSpot;
